import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Star, Quote, Loader2 } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import Seo from '../components/Seo';
import { testimonialsData } from '../data/testimonials';
import { countryCodeOf, countryName } from '../lib/countries';
import { Flag } from '../components/Flag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  es: {
    kicker: 'Opiniones',
    title: 'Lo que dicen mis alumnos',
    intro: 'Opiniones reales de alumnos de distintos países que aprenden español online conmigo.',
    seoTitle: 'Opiniones de alumnos | Clases de español online',
    seoDesc: 'Opiniones y valoraciones reales de alumnos que aprenden español online con Cecilia Mateo García, profesora y examinadora DELE.',
  },
  en: {
    kicker: 'Reviews',
    title: 'What my students say',
    intro: 'Real reviews from students around the world learning Spanish online with me.',
    seoTitle: 'Student reviews | Online Spanish lessons',
    seoDesc: 'Real reviews from students learning Spanish online with Cecilia Mateo García, Spanish teacher and accredited DELE examiner.',
  },
  de: {
    kicker: 'Bewertungen',
    title: 'Das sagen meine Lernenden',
    intro: 'Echte Bewertungen von Lernenden aus verschiedenen Ländern, die online mit mir Spanisch lernen.',
    seoTitle: 'Bewertungen von Lernenden | Spanisch online lernen',
    seoDesc: 'Echte Bewertungen von Lernenden, die mit Cecilia Mateo García, Spanischlehrerin und akkreditierte DELE-Prüferin, online Spanisch lernen.',
  },
};

export default function ReviewsPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.es;
  const [published, setPublished] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/public`);
        setPublished(data);
      } catch {
        setPublished([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const all = [...published, ...testimonialsData];

  return (
    <div className="min-h-screen bg-white">
      <Seo path="/opiniones" title={c.seoTitle} description={c.seoDesc} />
      <Navigation />

      <section className="pt-32 pb-10 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">{c.kicker}</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#4A4A4A] mt-3 mb-4" data-testid="reviews-title">{c.title}</h1>
          <p className="text-gray-600 max-w-2xl">{c.intro}</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-[#7DBDD9] animate-spin" /></div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5" data-testid="reviews-list">
              {all.map((r, i) => (
                <motion.div
                  key={`${r.name}-${i}`}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(i * 0.05, 0.4) }}
                  data-testid={`review-item-${i}`}
                  className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 relative"
                >
                  <Quote className="w-8 h-8 text-[#A8D8EA]/40 absolute top-5 right-5" />
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(r.rating || 5)].map((_, s) => (
                      <Star key={s} className="w-4 h-4 text-[#F5C14B] fill-[#F5C14B]" />
                    ))}
                  </div>
                  <p className="text-[#4A4A4A] mb-4 leading-relaxed">“{r.text}”</p>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#A8D8EA] to-[#7DBDD9] text-white flex items-center justify-center font-bold">
                      {r.name?.[0]}
                    </span>
                    <div>
                      <p className="font-bold text-[#4A4A4A]">{r.name}</p>
                      {r.country && (
                        <p className="text-sm text-gray-500 flex items-center gap-1.5" data-testid={`review-country-${i}`}>
                          <Flag code={r.country_code || r.countryCode || countryCodeOf(r.country)} title={r.country} />
                          {countryName((r.country_code || r.countryCode || countryCodeOf(r.country))?.toLowerCase(), language) || r.country}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
